import React, { Suspense, useState, useEffect, useMemo, useRef } from 'react';
import { Layout as AntLayout, Menu, Avatar, Dropdown, Drawer, Button, Tag, Input, message, Tooltip, Spin } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  HistoryOutlined,
  ShoppingCartOutlined,
  MenuOutlined,
  TeamOutlined,
  ScheduleOutlined,
  CrownOutlined,
  CoffeeOutlined,
  CloudOutlined,
  LineChartOutlined,
  NodeIndexOutlined,
  CalculatorOutlined,
  EnvironmentOutlined,
  SearchOutlined,
  UpOutlined,
  DownOutlined,
  CheckSquareOutlined,
  CalendarOutlined,
  FullscreenOutlined,
  PackageOutlined,
  FullscreenExitOutlined,
  LockOutlined,
  SwapOutlined,
  FileTextOutlined,
  InboxOutlined,
  DownloadOutlined,
  ThunderboltOutlined,
  SyncOutlined,
  TagOutlined,
  CopyOutlined,
  ToolOutlined,
  DashboardOutlined,
  AlertOutlined,
  FormOutlined,
  QrcodeOutlined,
  ApartmentOutlined,
  SafetyCertificateOutlined,
  TruckOutlined,
  UnorderedListOutlined,
  HomeOutlined,
  PieChartOutlined,
  BankOutlined,
  FundOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import MilkingMenuGroupLabel from './milking/MilkingMenuGroupLabel';
import { MILKING_MENU_ROUTE_OVERRIDES } from '../constants/milkingWorkflowSteps';
import '../styles/milking-workflow-guide.css';
import {
  GiCow,
  GiSheep,
  GiBarn,
  GiFarmTractor,
  GiNotebook,
  GiMoneyStack,
  GiWheat,
  GiRoundSilo,
  GiGrain,
  GiBarrel,
  GiCorn,
  GiPieChart,
  GiHouse,
  GiPerson,
  GiFactory,
  GiOfficeChair,
} from 'react-icons/gi';
import { FaUsersCog, FaUserShield, FaBook } from 'react-icons/fa';
import { TbReportAnalytics } from 'react-icons/tb';
import {
  MedicineBoxOutlined,
  AppleOutlined,
  CloseOutlined,
  ReadOutlined,
  ScanOutlined,
  HeartOutlined,
  BgColorsOutlined,
  DropboxOutlined,
  DollarOutlined,
  FallOutlined,
  PictureOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  DeleteOutlined,
  PlusOutlined,
  ShopOutlined,
  ExperimentOutlined,
  AimOutlined,
  BuildOutlined,
  WarningOutlined,
  BranchesOutlined,
  MobileOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { WORKER_MODE_PERMISSIONS } from './worker/workerShared';
import { isAccountOwner } from './OwnerOnly';
import { usePermissions } from '../hooks/usePermissions';
import { useAppData } from '../contexts/AppDataContext';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { getSidebarVariant } from '../theme/maveshiTheme';
import { SyncProgramModalProvider } from '../contexts/SyncProgramModalContext';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeSwitcher from './ThemeSwitcher';
import ThemeModeSwitcher from './ThemeModeSwitcher';
import GlobalSearch, { GlobalSearchBarTrigger, GlobalSearchIconTrigger } from './GlobalSearch';
import NotificationCenterBell from './NotificationCenterBell';
import i18n from '../i18n/config';
import { getSavedLanguage, resetDisplayLanguage } from '../utils/googleTranslate';
import MaveshiBrandLogo from './MaveshiBrandLogo';
import SidebarFarmSwitcher from './SidebarFarmSwitcher';
import PwaInstallFab from './PwaInstallFab';
import DemoFarmBanner from './DemoFarmBanner';
import FarmAssistantChat from './FarmAssistantChat';
import usePwaInstall from '../hooks/usePwaInstall';
import '../styles/pwa-install.css';

// Group heading inside the Breeding & Genetics menu, styled like the milking menu's groups.
const geneticsGroupLabel = (title, hint) => (
  <span className="milking-sidebar-group-oneline" title={`${title} — ${hint}`}>
    {title}
    <span className="milking-sidebar-group-hint-inline"> · {hint}</span>
  </span>
);

const { Header, Sider, Content } = AntLayout;

// Activities list for dropdown menu
const activitiesList = [
  { key: 'notes', label: 'Notes', icon: <ReadOutlined />, color: '#1890ff' },
  { key: 'observations', label: 'Observations', icon: <EyeOutlined />, color: '#6366f1', isNew: true },
  { key: 'treatments', label: 'Treatments', icon: <MedicineBoxOutlined />, color: '#f5222d' },
  { key: 'feedings', label: 'Feeding', icon: <AppleOutlined />, color: '#faad14', isNew: true },
  { key: 'measurements', label: 'Measurements', icon: <ScanOutlined />, color: '#13c2c2' },
  { key: 'breeding', label: 'Breeding', icon: <HeartOutlined />, color: '#eb2f96' },
  { key: 'sync-breeding', label: 'Estrus Sync', icon: <SyncOutlined />, color: '#fa8c16', isNew: true },
  { key: 'inputs', label: 'Inputs', icon: <BgColorsOutlined />, color: '#fa541c' },
  { key: 'yields', label: 'Yields', icon: <DropboxOutlined />, color: '#fa8c16' },
  { key: 'accounting', label: 'Accounting', icon: <DollarOutlined />, color: '#52c41a' },
  { key: 'photos', label: 'Photos', icon: <PictureOutlined />, color: '#13c2c2' },
  { key: 'status', label: 'Status', icon: <InfoCircleOutlined />, color: '#fa541c' },
];

const QUICK_ACTIVITY_REGISTRY_KEYS = [
  'measurements/registry',
  'breeding/registry',
  'movement/registry',
  'inputs/registry',
  'notes/registry',
  'status-dashboard/registry',
];

const QUICK_PEDIGREE_REGISTRY_KEYS = [
  'offspring/registry',
  'siblings/registry',
  'genealogy/registry',
];

// Menu item to parent mapping (moved outside component to prevent recreation on each render)
const menuItemToParent = {
  'tasks': 'dashboard-tasks',
  'daily-operations': 'dashboard-tasks',
  'schedule-dashboard': 'dashboard-tasks',
  'schedules/all': 'dashboard-tasks',
  'security': 'security-menu',
  'security/patrol': 'security-menu',
  'security/biosecurity': 'security-menu',
  'security/headcount': 'security-menu',
  'security/visitors': 'security-menu',
  'security/quarantine': 'security-menu',
  'weather': 'weather-menu',
  'hli-forecast': 'weather-menu',
  'thi-reference': 'weather-menu',
  'locations': 'farms-locations',
  'farms': 'farms-locations',
  'farms-locations-dashboard': 'farms-locations',
  'farm-map': 'farms-locations',
  'departments': 'reports-finance',
  'zones': 'farms-locations',
  'sheds': 'farms-locations',
  'lots': 'farms-locations',
  'pens': 'farms-locations',
  'animals/permanent': 'animals-menu',
  'animal-groups': 'animals-menu',
  'feedlots': 'feedlot-menu',
  'feedlots/dashboard': 'feedlot-menu',
  'feedlots/animals': 'feedlot-menu',
  'activities': 'animals-menu',
  'animal-dashboard': 'animals-menu',
  'tagging/dashboard': 'tagging-menu',
  'tagging/system': 'tagging-menu',
  'tagging/custom': 'tagging-menu',
  'tagging/cohort': 'tagging-menu',
  'tagging/performance': 'tagging-menu',
  'tagging/rules': 'tagging-menu',
  'cost-centres': 'reports-finance',
  'chart-of-accounts': 'reports-finance',
  'accounting-dashboard': 'reports-finance',
  'other-charges': 'reports-finance',
  'accounting-ledgers': 'reports-finance',
  'sales': 'reports-finance',
  'inventory-dashboard': 'inventory-menu',
  'inventory': 'inventory-menu',
  'inventory/receive': 'inventory-menu',
  'inventory/batches': 'inventory-menu',
  'inventory/usage': 'inventory-menu',
  'inventory/warehouses': 'inventory-menu',
  'reports': 'reports-finance',
  'maintenance': 'maintenance-menu',
  'maintenance/equipment': 'maintenance-menu',
  'maintenance/schedules': 'maintenance-menu',
  'milking': 'milking-menu',
  'milking/inventory': 'milking-menu',
  'milking/tanks': 'milking-menu',
  'milking/routing': 'milking-menu',
  'milking/balance': 'milking-menu',
  'milking/dispositions': 'milking-menu',
  'milking/buyers': 'milking-menu',
  'milking/collections': 'milking-menu',
  'milking/quality': 'milking-menu',
  'milking/tank-logs': 'milking-menu',
  'milking/workers': 'milking-menu',
  'milking/calves': 'milking-menu',
  'milking/processing': 'milking-menu',
  'milking/wastage': 'milking-menu',
  'animals/real-time-milking': 'milking-menu',
  'breeding': 'genetics-menu',
  'breeding/calendar': 'genetics-menu',
  'breeding/semen': 'genetics-menu',
  'breeding/nitrogen-tanks': 'genetics-menu',
  'breeding/genetics': 'genetics-menu',
  'breeding/programmes': 'genetics-menu',
  'breeding/population': 'genetics-menu',
  'breeding/pairs': 'genetics-menu',
  'breeding/population-search': 'genetics-menu',
  'breeding/animal': 'genetics-menu',
  'breeding/record': 'genetics-menu',
  'breeding/outcomes': 'genetics-menu',
  'breeding/male-evaluation': 'genetics-menu',
  'breeding/progeny': 'genetics-menu',
  'breeding/sires': 'genetics-menu',
  'sync-templates': 'synchronization-menu',
  'synchronization': 'synchronization-menu',
  'synchronization/calendar': 'synchronization-menu',
  'synchronization/history': 'synchronization-menu',
  'synchronization/templates': 'synchronization-menu',
  'sync-breeding': 'animals-menu',
  'observations': 'animals-menu',
  'feeding': 'feed-menu',
  'feed': 'feed-menu',
  'feed/daily': 'feed-menu',
  'feed/plans': 'feed-menu',
  'feed/forecast': 'feed-menu',
  'feed/library': 'feed-menu',
  'feed/least-cost': 'feed-menu',
  'feed/bunk': 'feed-menu',
  'feed/feeders': 'feed-menu',
  'feed/efficiency': 'feed-menu',
  'feed/stores/silage': 'feed-menu',
  'feed/stores/bales': 'feed-menu',
  'feed/stores/grain': 'feed-menu',
  'feed/stores/liquid': 'feed-menu',
  'feeding/registry': 'feed-menu',
  'measurements/registry': 'quick-activity-registers',
  'breeding/registry': 'quick-activity-registers',
  'feed-formulations': 'feed-menu',
  'wastage': 'feed-menu',
  'movement': 'farm-operations',
  'movement/registry': 'quick-activity-registers',
  'grazing': 'grazing-menu',
  'grazing/movement': 'grazing-menu',
  'grazing/chart': 'grazing-menu',
  'grazing/planner': 'grazing-menu',
  'grazing/stocking': 'grazing-menu',
  'grazing/paddocks': 'grazing-menu',
  'grazing/setup': 'grazing-menu',
  'drafting': 'drafting-menu',
  'drafting/split': 'drafting-menu',
  'drafting/merge': 'drafting-menu',
  'drafting/joining': 'drafting-menu',
  'drafting/yard-session': 'drafting-menu',
  'drafting/import': 'drafting-menu',
  'drafting/nlis': 'drafting-menu',
  'drafting/pics': 'drafting-menu',
  'drafting/history': 'drafting-menu',
  'health': 'health-menu',
  'health/session': 'health-menu',
  'health/withdrawals': 'health-menu',
  'health/records': 'health-menu',
  'health/add': 'health-menu',
  'inputs/registry': 'quick-activity-registers',
  'notes/registry': 'quick-activity-registers',
  'status-dashboard': 'farm-operations',
  'status-dashboard/registry': 'quick-activity-registers',
  'offspring/registry': 'pedigree-menu',
  'siblings/registry': 'pedigree-menu',
  'genealogy/registry': 'pedigree-menu',
  'measurements': 'farm-operations',
  'visitors': 'staff-teams',
  'staff-teams-dashboard': 'staff-teams',
  'team': 'staff-teams',
  'roster': 'staff-teams',
  'payroll': 'staff-teams',
  'contacts': 'staff-teams',
};

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, logout, switchUser } = useAuth();
  const { can, canAny, isSuperAdmin } = usePermissions();
  const { subscription, loadingSubscription } = useAppData();
  const { t } = useTranslation();
  const { isCompact, themeMode, colorScheme } = useTheme();
  const sidebarMenuTheme = getSidebarVariant(colorScheme) === 'light' ? 'light' : 'dark';

  // Activities list for dropdown menu (with translations)
  const translatedActivitiesList = [
    { key: 'notes', label: t('common.notes'), icon: <ReadOutlined />, color: '#1890ff' },
    { key: 'observations', label: 'Observations', icon: <EyeOutlined />, color: '#6366f1', isNew: true },
    { key: 'treatments', label: t('common.treatments'), icon: <MedicineBoxOutlined />, color: '#f5222d' },
    { key: 'feedings', label: t('common.feedings'), icon: <AppleOutlined />, color: '#faad14', isNew: true },
    { key: 'measurements', label: t('common.measurements'), icon: <ScanOutlined />, color: '#13c2c2' },
    { key: 'breeding', label: t('common.breeding'), icon: <HeartOutlined />, color: '#eb2f96' },
    { key: 'sync-breeding', label: 'Estrus Sync', icon: <SyncOutlined />, color: '#fa8c16', isNew: true },
    { key: 'inputs', label: t('common.inputs'), icon: <BgColorsOutlined />, color: '#fa541c' },
    { key: 'yields', label: t('common.yields'), icon: <DropboxOutlined />, color: '#fa8c16' },
    { key: 'accounting', label: t('common.accounting'), icon: <DollarOutlined />, color: '#52c41a' },
    { key: 'photos', label: t('common.photos'), icon: <PictureOutlined />, color: '#13c2c2' },
    { key: 'status', label: t('common.status'), icon: <InfoCircleOutlined />, color: '#fa541c' },
  ];

  // Check if account is locked
  // Only disable actions if subscription has finished loading AND is actually locked/not active
  // Don't disable during loading phase
  const isAccountLocked = subscription?.is_locked === true;
  const isNotActive = subscription && !subscription.is_fully_active; // Only check if subscription exists
  const shouldDisableActions = !loadingSubscription && (isAccountLocked || isNotActive) && !isSuperAdmin;

  // Check if currently impersonating
  const isImpersonating = localStorage.getItem('original_admin_token') !== null;
  const originalAdminUser = isImpersonating ? JSON.parse(localStorage.getItem('original_admin_user') || '{}') : null;

  // Function to return to admin account
  const returnToAdmin = () => {
    const originalToken = localStorage.getItem('original_admin_token');
    const originalUser = JSON.parse(localStorage.getItem('original_admin_user') || '{}');

    if (originalToken && originalUser) {
      // Clear impersonation data
      localStorage.removeItem('original_admin_token');
      localStorage.removeItem('original_admin_user');

      // Full page load back to the admin Users page as the original admin
      switchUser(originalToken, originalUser, '/users');
    }
  };

  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [globalSearchListening, setGlobalSearchListening] = useState(false);
  const globalSearchRef = useRef(null);
  const prevMenuOpenKeysRef = useRef([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const {
    showInstallFab,
    isInstallAvailable,
    manualInstallMode,
    helpModalMode,
    helpOpen,
    setHelpOpen,
    installApp,
    dismissPromo,
  } = usePwaInstall();

  // Load menu state from localStorage
  useEffect(() => {
    const savedOpenKeys = JSON.parse(localStorage.getItem('menuOpenKeys') || '[]');
    const savedSelectedKeys = JSON.parse(localStorage.getItem('menuSelectedKeys') || '[]');

    setOpenKeys(savedOpenKeys);
    setSelectedKeys(savedSelectedKeys);
  }, []);

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(collapsed));

    // Close all submenus when sidebar collapses
    if (collapsed) {
      setOpenKeys([]);
      localStorage.setItem('menuOpenKeys', JSON.stringify([]));
    } else {
      // When expanded, restore open keys from localStorage
      const savedOpenKeys = JSON.parse(localStorage.getItem('menuOpenKeys') || '[]');
      if (savedOpenKeys.length > 0) {
        setOpenKeys(savedOpenKeys);
      }
    }
  }, [collapsed]);

  // Check if account is locked and redirect to upgrade page
  useEffect(() => {
    // Don't redirect if still loading subscription or if user is super admin
    if (loadingSubscription || isSuperAdmin || !user) {
      return;
    }

    // Allow access to upgrade page, profile/settings, and dashboard (for wizard)
    const allowedPaths = ['/upgrade', '/me', '/settings', '/change-password', '/'];
    if (allowedPaths.some(path => location.pathname === path || location.pathname.startsWith(path))) {
      // Only check subscription status for upgrade page, not for dashboard
      if (location.pathname === '/' || location.pathname.startsWith('/')) {
        return; // Allow dashboard access - it will handle wizard/showing upgrade if needed
      }
      return;
    }

    // Check if account is locked
    if (subscription && subscription.is_locked) {
      navigate('/upgrade', { replace: true });
      return;
    }

    // Check if subscription is not active
    if (subscription && !subscription.is_fully_active) {
      navigate('/upgrade', { replace: true });
      return;
    }

    // Only redirect if subscription is confirmed missing (not just loading)
    // Don't redirect for dashboard - let it handle the wizard/upgrade flow
    if (!subscription && user && !isSuperAdmin && location.pathname !== '/') {
      navigate('/upgrade', { replace: true });
      return;
    }
  }, [subscription, loadingSubscription, isSuperAdmin, user, location.pathname, navigate]);

  // Listen for account-locked events from API interceptor
  useEffect(() => {
    const handleAccountLocked = (event) => {
      const { upgradeUrl } = event.detail;
      // Only navigate if not already on upgrade page (check if path starts with /upgrade)
      if (!location.pathname.startsWith('/upgrade')) {
        navigate(upgradeUrl, { replace: true });
      }
    };

    window.addEventListener('account-locked', handleAccountLocked);
    return () => {
      window.removeEventListener('account-locked', handleAccountLocked);
    };
  }, [navigate, location.pathname]);

  // Save open keys to localStorage when they change (but skip initial load)
  useEffect(() => {
    // Only save if openKeys has been initialized (not empty from initial state)
    // This prevents overwriting localStorage on initial mount
    if (openKeys.length > 0 || localStorage.getItem('menuOpenKeys')) {
      localStorage.setItem('menuOpenKeys', JSON.stringify(openKeys));
    }
  }, [openKeys]);

  // Save menu state to localStorage
  const saveMenuState = (newOpenKeys, newSelectedKeys) => {
    localStorage.setItem('menuOpenKeys', JSON.stringify(newOpenKeys));
    localStorage.setItem('menuSelectedKeys', JSON.stringify(newSelectedKeys));
  };

  // Detect screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);

      // Auto-collapse sidebar on mobile/tablet
      if (width < 1024) {
        setCollapsed(true);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Close drawer when route changes on mobile/tablet
  useEffect(() => {
    if ((isMobile || isTablet) && mobileDrawerVisible) {
      setMobileDrawerVisible(false);
    }
  }, [location.pathname, isMobile, isTablet]);

  // Update selected keys and auto-open parent menu when route changes
  useEffect(() => {
    const path = location.pathname;
    let newSelectedKeys = [];

    if (path === '/') {
      newSelectedKeys = ['/'];
    } else {
      // Remove leading slash and split
      const pathParts = path.replace(/^\//, '').split('/');

      // For super admin routes like /admin/packages, use the full path 'admin/packages'
      // For regular routes like /farms, use just 'farms'
      if (pathParts.length > 1 && pathParts[0] === 'admin') {
        // Super admin nested routes: admin/packages, admin/subscriptions, etc.
        newSelectedKeys = [pathParts.join('/')];

        // Auto-open the parent menu (admin-packages)
        setOpenKeys(prevOpenKeys => {
          if (!prevOpenKeys.includes('admin-packages')) {
            return [...prevOpenKeys, 'admin-packages'];
          }
          return prevOpenKeys;
        });
      } else if (pathParts.length > 1 && pathParts[0] === 'maintenance') {
        // Maintenance nested routes: maintenance/equipment, maintenance/schedules
        newSelectedKeys = [pathParts.join('/')];

        // Auto-open the parent menu (maintenance-menu)
        setOpenKeys(prevOpenKeys => {
          if (!prevOpenKeys.includes('maintenance-menu')) {
            return [...prevOpenKeys, 'maintenance-menu'];
          }
          return prevOpenKeys;
        });
      } else if (pathParts.length > 1 && pathParts[0] === 'schedules') {
        // Schedule routes: schedules/all
        newSelectedKeys = [pathParts.join('/')];

        // Auto-open the parent menu (dashboard-tasks)
        setOpenKeys(prevOpenKeys => {
          if (!prevOpenKeys.includes('dashboard-tasks')) {
            return [...prevOpenKeys, 'dashboard-tasks'];
          }
          return prevOpenKeys;
        });
      } else if (pathParts[0] === 'breeding') {
        newSelectedKeys = pathParts.length > 1 ? [pathParts.join('/')] : ['breeding'];
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('genetics-menu')) {
            return [...prevOpenKeys, 'genetics-menu'];
          }
          return prevOpenKeys;
        });
      } else if (pathParts[0] === 'grazing') {
        newSelectedKeys = pathParts.length > 1 ? [pathParts.join('/')] : ['grazing'];
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('grazing-menu')) {
            return [...prevOpenKeys, 'grazing-menu'];
          }
          return prevOpenKeys;
        });
      } else if (pathParts[0] === 'drafting') {
        newSelectedKeys = pathParts.length > 1 ? [`drafting/${pathParts[1]}`] : ['drafting'];
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('drafting-menu')) {
            return [...prevOpenKeys, 'drafting-menu'];
          }
          return prevOpenKeys;
        });
      } else if (pathParts[0] === 'health') {
        // Animal health detail opens from the records, so keep that item lit
        const sub = pathParts[1] === 'animals' ? 'records' : pathParts[1];
        newSelectedKeys = sub ? [`health/${sub}`] : ['health'];
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('health-menu')) {
            return [...prevOpenKeys, 'health-menu'];
          }
          return prevOpenKeys;
        });
      } else if (pathParts[0] === 'grazing-manager') {
        newSelectedKeys = ['grazing'];
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('grazing-menu')) {
            return [...prevOpenKeys, 'grazing-menu'];
          }
          return prevOpenKeys;
        });
      } else if (pathParts[0] === 'synchronization' || pathParts[0] === 'sync-templates') {
        if (pathParts[0] === 'sync-templates') {
          newSelectedKeys = ['synchronization/templates'];
        } else if (pathParts[1] === 'program') {
          const from = new URLSearchParams(location.search).get('from');
          newSelectedKeys = from && ['calendar', 'history'].includes(from)
            ? [`synchronization/${from}`]
            : ['synchronization'];
        } else {
          newSelectedKeys = pathParts.length > 1 ? [pathParts.join('/')] : ['synchronization'];
        }
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('synchronization-menu')) {
            return [...prevOpenKeys, 'synchronization-menu'];
          }
          return prevOpenKeys;
        });
      } else if (
        pathParts[0] === 'feed'
        || pathParts[0] === 'feeding'
        || pathParts[0] === 'feed-formulations'
        || pathParts[0] === 'wastage'
      ) {
        // The day view and history are tabs of Feed overview.
        if (pathParts[0] === 'feed' && pathParts[1] === 'daily') newSelectedKeys = ['feed'];
        else if (pathParts[0] === 'feed' && pathParts[1] === 'plans') newSelectedKeys = ['feed/plans'];
        else if (pathParts[0] === 'feed' && pathParts[1] === 'forecast') newSelectedKeys = ['feed/forecast'];
        else if (pathParts[0] === 'feed' && pathParts[1] === 'library') newSelectedKeys = ['feed/library'];
        else if (pathParts[0] === 'feed' && pathParts[1] === 'least-cost') newSelectedKeys = ['feed/least-cost'];
        else if (pathParts[0] === 'feed' && pathParts[1] === 'bunk') newSelectedKeys = ['feed/bunk'];
        else if (pathParts[0] === 'feed' && pathParts[1] === 'feeders') newSelectedKeys = ['feed/feeders'];
        else if (pathParts[0] === 'feed' && pathParts[1] === 'efficiency') newSelectedKeys = ['feed/efficiency'];
        else if (pathParts[0] === 'feed' && pathParts[1] === 'stores') newSelectedKeys = [`feed/stores/${pathParts[2] || 'silage'}`];
        else if (pathParts[0] === 'feeding' && pathParts[1] === 'registry') newSelectedKeys = ['feeding/registry'];
        else if (pathParts[0] === 'feed' || (pathParts[0] === 'feeding' && pathParts.length === 1)) newSelectedKeys = ['feed'];
        else newSelectedKeys = [pathParts[0]];
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('feed-menu')) {
            return [...prevOpenKeys, 'feed-menu'];
          }
          return prevOpenKeys;
        });
      } else if (pathParts[0] === 'field-reports') {
        newSelectedKeys = pathParts.length > 1 ? [pathParts.join('/')] : ['field-reports/alerts'];
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('field-reports-menu')) {
            return [...prevOpenKeys, 'field-reports-menu'];
          }
          return prevOpenKeys;
        });
      } else if (pathParts[0] === 'alerts') {
        newSelectedKeys = ['field-reports/alerts'];
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('field-reports-menu')) {
            return [...prevOpenKeys, 'field-reports-menu'];
          }
          return prevOpenKeys;
        });
      } else if (pathParts[0] === 'executive-dashboard') {
        newSelectedKeys = ['executive-dashboard'];
      } else if (pathParts[0] === 'tasks' || pathParts[0] === 'daily-operations') {
        newSelectedKeys = [pathParts[0]];
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('dashboard-tasks')) {
            return [...prevOpenKeys, 'dashboard-tasks'];
          }
          return prevOpenKeys;
        });
      } else if (pathParts[0] === 'measurements') {
        newSelectedKeys = ['measurements'];
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('farm-operations')) {
            return [...prevOpenKeys, 'farm-operations'];
          }
          return prevOpenKeys;
        });
      } else if (pathParts[0] === 'milking') {
        // /milking and /milking?tab=balance are the same overview page — always highlight overview
        newSelectedKeys =
          pathParts.length > 1 && pathParts[1] !== 'inventory'
            ? [pathParts.join('/')]
            : ['milking'];
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('milking-menu')) {
            return [...prevOpenKeys, 'milking-menu'];
          }
          return prevOpenKeys;
        });
      } else if (pathParts.length > 1 && pathParts[0] === 'animals' && pathParts[1] === 'real-time-milking') {
        newSelectedKeys = ['animals/real-time-milking'];
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('milking-menu')) {
            return [...prevOpenKeys, 'milking-menu'];
          }
          return prevOpenKeys;
        });
      } else if (QUICK_ACTIVITY_REGISTRY_KEYS.includes(pathParts.join('/'))) {
        newSelectedKeys = [pathParts.join('/')];
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('quick-activity-registers')) {
            return [...prevOpenKeys, 'quick-activity-registers'];
          }
          return prevOpenKeys;
        });
      } else if (QUICK_PEDIGREE_REGISTRY_KEYS.includes(pathParts.join('/'))) {
        newSelectedKeys = [pathParts.join('/')];
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('pedigree-menu')) {
            return [...prevOpenKeys, 'pedigree-menu'];
          }
          return prevOpenKeys;
        });
      } else if (
        ['movement', 'measurements', 'breeding', 'status-dashboard'].includes(pathParts[0])
      ) {
        newSelectedKeys = [pathParts[0]];
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('farm-operations')) {
            return [...prevOpenKeys, 'farm-operations'];
          }
          return prevOpenKeys;
        });
      } else if (pathParts.length > 1 && pathParts[0] === 'animals' && pathParts[1] === 'permanent') {
        newSelectedKeys = ['animals/permanent'];
        setOpenKeys(prevOpenKeys => {
          if (!prevOpenKeys.includes('animals-menu')) {
            return [...prevOpenKeys, 'animals-menu'];
          }
          return prevOpenKeys;
        });
      } else if (path === '/inventory-dashboard' || pathParts[0] === 'inventory-dashboard') {
        newSelectedKeys = ['inventory-dashboard'];
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('inventory-menu')) {
            return [...prevOpenKeys, 'inventory-menu'];
          }
          return prevOpenKeys;
        });
      } else if (pathParts[0] === 'inventory') {
        if (pathParts[1] === 'receive') {
          newSelectedKeys = ['inventory/receive'];
        } else if (pathParts[1] === 'batches') {
          newSelectedKeys = ['inventory/batches'];
        } else if (pathParts[1] === 'usage') {
          newSelectedKeys = ['inventory/usage'];
        } else if (pathParts[1] === 'warehouses') {
          newSelectedKeys = ['inventory/warehouses'];
        } else {
          newSelectedKeys = ['inventory'];
        }
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('inventory-menu')) {
            return [...prevOpenKeys, 'inventory-menu'];
          }
          return prevOpenKeys;
        });
      } else if (pathParts[0] === 'tagging') {
        newSelectedKeys = pathParts.length > 1 ? [pathParts.join('/')] : ['tagging/dashboard'];
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('tagging-menu')) {
            return [...prevOpenKeys, 'tagging-menu'];
          }
          return prevOpenKeys;
        });
      } else if (pathParts[0] === 'feedlots') {
        if (pathParts[1] === 'dashboard') {
          newSelectedKeys = ['feedlots/dashboard'];
        } else if (pathParts[1] === 'animals') {
          newSelectedKeys = ['feedlots/animals'];
        } else {
          newSelectedKeys = ['feedlots'];
        }
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('feedlot-menu')) {
            return [...prevOpenKeys, 'feedlot-menu'];
          }
          return prevOpenKeys;
        });
      } else if (pathParts[0] === 'finance') {
        newSelectedKeys = [pathParts.slice(0, 2).join('/')];
        setOpenKeys((prevOpenKeys) => (prevOpenKeys.includes('reports-finance') ? prevOpenKeys : [...prevOpenKeys, 'reports-finance']));
      } else if (pathParts[0] === 'security') {
        if (pathParts.length === 1) {
          newSelectedKeys = ['security'];
        } else {
          newSelectedKeys = [pathParts.join('/')];
        }
        setOpenKeys((prevOpenKeys) => {
          if (!prevOpenKeys.includes('security-menu')) {
            return [...prevOpenKeys, 'security-menu'];
          }
          return prevOpenKeys;
        });
      } else {
        // Regular routes: extract the first path segment
        const pathSegment = pathParts[0];
        if (pathSegment) {
          newSelectedKeys = [pathSegment];

          // Auto-open the parent menu for this item (only if not already open)
          const parentKey = menuItemToParent[pathSegment];
          if (parentKey) {
            setOpenKeys(prevOpenKeys => {
              const keysToOpen = [];
              if (!prevOpenKeys.includes(parentKey)) {
                keysToOpen.push(parentKey);
              }
              const grandParentKey = menuItemToParent[parentKey];
              if (grandParentKey && !prevOpenKeys.includes(grandParentKey)) {
                keysToOpen.push(grandParentKey);
              }
              if (keysToOpen.length === 0) {
                return prevOpenKeys;
              }
              return [...prevOpenKeys, ...keysToOpen];
            });
          }
        }
      }
    }

    setSelectedKeys(newSelectedKeys);
    localStorage.setItem('menuSelectedKeys', JSON.stringify(newSelectedKeys));
  }, [location.pathname, location.search]);

  // Check fullscreen state on mount and changes
  useEffect(() => {
    const checkFullscreen = () => {
      const doc = document;
      const isCurrentlyFullscreen = !!(
        doc.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.mozFullScreenElement ||
        doc.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    // Initial check
    checkFullscreen();

    // Listen to all fullscreen change events
    document.addEventListener('fullscreenchange', checkFullscreen);
    document.addEventListener('webkitfullscreenchange', checkFullscreen);
    document.addEventListener('mozfullscreenchange', checkFullscreen);
    document.addEventListener('MSFullscreenChange', checkFullscreen);

    return () => {
      document.removeEventListener('fullscreenchange', checkFullscreen);
      document.removeEventListener('webkitfullscreenchange', checkFullscreen);
      document.removeEventListener('mozfullscreenchange', checkFullscreen);
      document.removeEventListener('MSFullscreenChange', checkFullscreen);
    };
  }, []);

  // Toggle fullscreen function
  const toggleFullscreen = () => {
    const doc = document;

    // Check actual DOM state
    const isCurrentlyFullscreen = !!(
      doc.fullscreenElement ||
      doc.webkitFullscreenElement ||
      doc.mozFullScreenElement ||
      doc.msFullscreenElement
    );

    console.log('Fullscreen toggle clicked. Current state:', isCurrentlyFullscreen);

    if (!isCurrentlyFullscreen) {
      // Enter fullscreen
      const element = doc.documentElement;

      if (element.requestFullscreen) {
        console.log('Attempting to enter fullscreen using standard API');
        element.requestFullscreen().catch(err => {
          console.error('Error entering fullscreen:', err.name, err.message);
        });
      } else if (element.webkitRequestFullscreen) {
        console.log('Attempting to enter fullscreen using webkit API');
        element.webkitRequestFullscreen();
      } else if (element.mozRequestFullScreen) {
        console.log('Attempting to enter fullscreen using moz API');
        element.mozRequestFullScreen();
      } else if (element.msRequestFullscreen) {
        console.log('Attempting to enter fullscreen using ms API');
        element.msRequestFullscreen();
      } else {
        console.warn('Fullscreen API is not supported in this browser');
      }
    } else {
      // Exit fullscreen
      console.log('Attempting to exit fullscreen');
      if (doc.exitFullscreen) {
        doc.exitFullscreen().catch(err => {
          console.error('Error exiting fullscreen:', err.name, err.message);
        });
      } else if (doc.webkitExitFullscreen) {
        doc.webkitExitFullscreen();
      } else if (doc.mozCancelFullScreen) {
        doc.mozCancelFullScreen();
      } else if (doc.msExitFullscreen) {
        doc.msExitFullscreen();
      }
    }
  };

  const iconStyle = { fontSize: isCompact ? '16px' : '20px' };
  const parentIconStyle = { fontSize: isCompact ? '18px' : '22px' };

  // Helper function to mark menu items (but keep them clickable)
  // The actual disabling logic is handled in handleMenuClick
  const disableMenuItems = (items) => {
    // Don't modify items - keep them clickable
    // The handleMenuClick will handle showing the message
    return items;
  };

  const getMenuItemLabelText = (label) => (
    typeof label === 'string' ? label : ''
  ).toLowerCase();

  // Filter menu items based on search
  const filterMenuItems = (items, searchTerm) => {
    if (!searchTerm) return items;

    const term = searchTerm.toLowerCase();

    const filterChildren = (children) => {
      const filteredChildren = [];
      children.forEach(child => {
        const childLabel = getMenuItemLabelText(child.label);
        const childMatches = childLabel.includes(term);

        if (child.children) {
          const nestedChildren = filterChildren(child.children);
          if (childMatches || nestedChildren.length > 0) {
            filteredChildren.push({
              ...child,
              children: childMatches ? child.children : nestedChildren,
            });
          }
        } else if (childMatches) {
          filteredChildren.push(child);
        }
      });
      return filteredChildren;
    };

    const filtered = [];
    items.forEach(item => {
      if (item.type === 'divider') {
        return;
      }

      const label = getMenuItemLabelText(item.label);
      const matchesSearch = label.includes(term);

      if (item.children) {
        const filteredChildren = filterChildren(item.children);

        if (matchesSearch || filteredChildren.length > 0) {
          filtered.push({
            ...item,
            children: matchesSearch ? item.children : filteredChildren,
          });
        }
      } else if (matchesSearch) {
        filtered.push(item);
      }
    });

    return filtered;
  };

  // Build menu items based on permissions
  const menuItems = useMemo(() => {
    const items = [];

    // Super Admin Menu - Dashboard, Users, Packages, Subscriptions, Payments
    if (isSuperAdmin) {
      // Dashboard
      items.push({
        key: '/',
        icon: <GiHouse style={parentIconStyle} />,
        label: t('common.dashboard'),
      });

      // Users
      items.push({
        key: 'users',
        icon: <UserOutlined style={parentIconStyle} />,
        label: t('common.users'),
      });

      // Package Management
      items.push({
        key: 'admin-packages',
        icon: <CrownOutlined style={parentIconStyle} />,
        label: t('common.packages'),
        children: [
          {
            key: 'admin/packages',
            label: t('common.managePackages'),
          },
          {
            key: 'admin/subscriptions',
            label: t('common.subscriptions'),
          },
          {
            key: 'admin/payments',
            label: t('common.payments'),
          },
          {
            key: 'admin/upgrade-requests',
            label: t('common.upgradeRequests'),
          },
        ],
      });

      return items;
    }

    // Regular User Menu — quick access first, then weather, then grouped sections

    const quickAccessItems = [];
    if (can('view dashboard')) {
      quickAccessItems.push({
        key: '/',
        icon: <GiHouse style={iconStyle} />,
        label: t('common.dashboard') || 'Dashboard',
      });
    }

    // Big-button quick entry for farm workers (milk, feed given, sick / injured)
    if (canAny(WORKER_MODE_PERMISSIONS)) {
      quickAccessItems.push({
        key: 'worker',
        icon: <MobileOutlined style={iconStyle} />,
        label: 'Worker mode',
      });
    }

    if (can('view reports')) {
      quickAccessItems.push({
        key: 'executive-dashboard',
        icon: <LineChartOutlined style={iconStyle} />,
        label: 'Executive Dashboard',
      });
    }

    if (can('view farms')) {
      quickAccessItems.push({
        key: 'farm-map',
        icon: <EnvironmentOutlined style={iconStyle} />,
        label: t('common.farmMap') || 'Farm Map',
      });
    }

    if (can('view animals')) {
      quickAccessItems.push({
        key: 'animals',
        icon: <GiCow style={iconStyle} />,
        label: t('common.animals') || 'Animals',
      });
    }

    const activityRegisterItems = [];
    if (can('view animal measurements')) {
      activityRegisterItems.push({
        key: 'measurements/registry',
        icon: <ScanOutlined style={iconStyle} />,
        label: 'Measurement Records',
      });
    }
    if (can('view animal breeding')) {
      activityRegisterItems.push({
        key: 'breeding/registry',
        icon: <HeartOutlined style={iconStyle} />,
        label: 'Breeding Records',
      });
    }
    if (can('view animal movements')) {
      activityRegisterItems.push({
        key: 'movement/registry',
        icon: <SwapOutlined style={iconStyle} />,
        label: 'Movement Records',
      });
    }
    if (can('view animal inputs')) {
      activityRegisterItems.push({
        key: 'inputs/registry',
        icon: <BgColorsOutlined style={iconStyle} />,
        label: 'Input Records',
      });
    }
    if (can('view animal notes')) {
      activityRegisterItems.push({
        key: 'notes/registry',
        icon: <ReadOutlined style={iconStyle} />,
        label: 'Note Records',
      });
    }
    if (can('view animals')) {
      activityRegisterItems.push({
        key: 'status-dashboard/registry',
        icon: <InfoCircleOutlined style={iconStyle} />,
        label: 'Status Records',
      });
    }
    if (activityRegisterItems.length > 0) {
      quickAccessItems.push({
        key: 'quick-activity-registers',
        icon: <FormOutlined style={iconStyle} />,
        label: 'Activity Records',
        children: activityRegisterItems,
      });
    }

    if (can('view animal schedule')) {
      quickAccessItems.push({
        key: 'schedule',
        icon: <CalendarOutlined style={iconStyle} />,
        label: t('common.calendar') || 'Calendar',
      });
    }
   
    if (quickAccessItems.length > 0) {
      items.push({
        type: 'group',
        label: t('common.quickAccess') || 'Quick Access',
        children: quickAccessItems,
      });
      items.push({ type: 'divider' });
    }

   

    if (can('view animals')) {
      items.push({
        key: 'animals-menu',
        icon: <GiCow style={parentIconStyle} />,
        label: t('common.animalManagement') || 'Animal Management',
        children: [
          { key: 'animal-dashboard', icon: <DashboardOutlined style={iconStyle} />, label: t('common.animalDashboard') || 'Dashboard' },
          { key: 'activities', icon: <CheckSquareOutlined style={iconStyle} />, label: t('common.activities') || 'Activities' },
          { key: 'animal-groups', icon: <GiSheep style={iconStyle} />, label: t('common.animalGroups') || 'Animal Groups' },
          { key: 'animals/permanent', icon: <GiCow style={{ ...iconStyle, opacity: 0.65 }} />, label: t('common.inactiveAnimals') || 'Inactive & archived' },
        ],
      });
    }

    // Farm Operations — single submenu level (no nested groups)
    if (can('view animals')) {
      items.push({
        key: 'farm-operations',
        icon: <ThunderboltOutlined style={parentIconStyle} />,
        label: t('common.farmOperations') || 'Farm Operations',
        children: [
          { key: 'measurements', icon: <ScanOutlined style={iconStyle} />, label: t('measurements.dashboard_short') || 'Measurements' },
          { key: 'status-dashboard', icon: <DashboardOutlined style={iconStyle} />, label: t('status.dashboard_short') || 'Animal Status' },
          { key: 'movement', icon: <SwapOutlined style={iconStyle} />, label: t('movement.dashboard_short') || 'Movements' },
        ],
      });
    }

    if (can('view animal treatments')) {
      items.push({
        key: 'health-menu',
        icon: <MedicineBoxOutlined style={parentIconStyle} />,
        label: 'Health & Treatment',
        children: [
          { key: 'health', icon: <DashboardOutlined style={iconStyle} />, label: 'Dashboard' },
          { key: 'health/session', icon: <MedicineBoxOutlined style={iconStyle} />, label: 'Treatment session' },
          { key: 'health/withdrawals', icon: <AlertOutlined style={iconStyle} />, label: 'Withdrawal tracker' },
          { key: 'health/records', icon: <UnorderedListOutlined style={iconStyle} />, label: 'Treatment records' },
          ...(can('create animal treatments')
            ? [{ key: 'health/add', icon: <PlusOutlined style={iconStyle} />, label: 'Add treatment' }]
            : []),
        ],
      });
    }

    if (canAny(['view animals', 'view animal breeding'])) {
      items.push({
        key: 'genetics-menu',
        icon: <ExperimentOutlined style={parentIconStyle} />,
        label: 'Breeding & Genetics',
        className: 'genetics-menu-root',
        popupClassName: 'maveshi-milking-submenu-popup',
        children: [
          { key: 'breeding', icon: <DashboardOutlined style={iconStyle} />, label: 'Breeding overview' },
          {
            type: 'group',
            key: 'genetics-group-herd',
            label: geneticsGroupLabel('Herd genetics', 'where the herd stands'),
            children: [
              { key: 'breeding/population', icon: <ApartmentOutlined style={iconStyle} />, label: 'Genetics overview' },
              { key: 'breeding/population-search', icon: <UnorderedListOutlined style={iconStyle} />, label: 'Population search' },
              { key: 'breeding/animal', icon: <ExperimentOutlined style={iconStyle} />, label: 'Animal genetics' },
              { key: 'breeding/pairs', icon: <SearchOutlined style={iconStyle} />, label: 'Find breeding pairs' },
            ],
          },
          {
            type: 'group',
            key: 'genetics-group-setup',
            label: geneticsGroupLabel('Sires & semen', 'what you breed with'),
            children: [
              { key: 'breeding/sires', icon: <CrownOutlined style={iconStyle} />, label: 'Sire directory' },
              { key: 'breeding/semen', icon: <ExperimentOutlined style={iconStyle} />, label: 'Semen catalog' },
              { key: 'breeding/nitrogen-tanks', icon: <InboxOutlined style={iconStyle} />, label: 'Nitrogen tanks' },
              { key: 'breeding/male-evaluation', icon: <LineChartOutlined style={iconStyle} />, label: 'Male evaluation' },
              { key: 'breeding/progeny', icon: <NodeIndexOutlined style={iconStyle} />, label: 'Progeny tracing' },
            ],
          },
          {
            type: 'group',
            key: 'genetics-group-plan',
            label: geneticsGroupLabel('Plan', 'which female goes to which sire'),
            children: [
              { key: 'breeding/programmes', icon: <AimOutlined style={iconStyle} />, label: 'Breeding programmes' },
              { key: 'breeding/record', icon: <PlusOutlined style={iconStyle} />, label: 'Record mating' },
            ],
          },
          {
            type: 'group',
            key: 'genetics-group-results',
            label: geneticsGroupLabel('Follow up', 'pregnancies and results'),
            children: [
              { key: 'breeding/calendar', icon: <CalendarOutlined style={iconStyle} />, label: 'Breeding calendar' },
              { key: 'breeding/outcomes', icon: <HeartOutlined style={iconStyle} />, label: 'Births & losses' },
              { key: 'breeding/genetics', icon: <HeartOutlined style={iconStyle} />, label: 'Sire & dam results' },
            ],
          },
        ],
      });
    }

    if (can('view animal feedings')) {
      items.push({
        key: 'feed-menu',
        icon: <CoffeeOutlined style={parentIconStyle} />,
        label: 'Feed',
        children: [
          {
            type: 'group',
            key: 'feed-group-daily',
            label: geneticsGroupLabel('Daily feeding', 'what was fed'),
            children: [
              { key: 'feed', icon: <DashboardOutlined style={iconStyle} />, label: 'Feed overview' },
              { key: 'feed-record', icon: <PlusOutlined style={iconStyle} />, label: 'Record feeding' },
              { key: 'feed/bunk', icon: <InboxOutlined style={iconStyle} />, label: 'Offered & leftovers' },
              { key: 'feed/feeders', icon: <InboxOutlined style={iconStyle} />, label: 'Feeders & troughs' },
              { key: 'feeding/registry', icon: <UnorderedListOutlined style={iconStyle} />, label: 'Feeding records' },
              { key: 'wastage', icon: <DeleteOutlined style={iconStyle} />, label: 'Feed wastage' },
            ],
          },
          {
            type: 'group',
            key: 'feed-group-plan',
            label: geneticsGroupLabel('Plan', 'who gets what'),
            children: [
              { key: 'feed/plans', icon: <ScheduleOutlined style={iconStyle} />, label: 'Feed planner' },
            ],
          },
          {
            type: 'group',
            key: 'feed-group-nutrition',
            label: geneticsGroupLabel('Mixes & nutrition', 'what goes in the mix'),
            children: [
              { key: 'feed-formulations', icon: <GiWheat style={iconStyle} />, label: 'Feed mixes' },
              { key: 'feed/library', icon: <ExperimentOutlined style={iconStyle} />, label: 'Feed library' },
              { key: 'feed/least-cost', icon: <CalculatorOutlined style={iconStyle} />, label: 'Least-cost ration' },
            ],
          },
          {
            type: 'group',
            key: 'feed-group-roughage',
            label: geneticsGroupLabel('Silage & roughage', 'what is in store'),
            children: [
              { key: 'feed/stores/silage', icon: <GiCorn style={iconStyle} />, label: 'Silage bunkers & pits' },
              { key: 'feed/stores/bales', icon: <GiGrain style={iconStyle} />, label: 'Bales & stacks' },
            ],
          },
          {
            type: 'group',
            key: 'feed-group-concentrate',
            label: geneticsGroupLabel('Concentrate & grain', 'what is in store'),
            children: [
              { key: 'feed/stores/grain', icon: <GiRoundSilo style={iconStyle} />, label: 'Grain bins & silos' },
              { key: 'feed/stores/liquid', icon: <GiBarrel style={iconStyle} />, label: 'Liquid stores' },
            ],
          },
          {
            type: 'group',
            key: 'feed-group-stock',
            label: geneticsGroupLabel('Stock & cost', 'what it costs'),
            children: [
              { key: 'feed/forecast', icon: <LineChartOutlined style={iconStyle} />, label: 'Ingredient forecast' },
              { key: 'feed/efficiency', icon: <DollarOutlined style={iconStyle} />, label: 'Efficiency & cost' },
            ],
          },
        ],
      });
    }

    // Schedule & Planning
    const dashboardTaskChildren = [];
    if (can('view animals')) {
      dashboardTaskChildren.push({
        key: 'schedule-dashboard',
        icon: <DashboardOutlined style={iconStyle} />,
        label: t('common.scheduleDashboard') || 'Dashboard',
      });
      dashboardTaskChildren.push({
        key: 'daily-operations',
        icon: <ThunderboltOutlined style={iconStyle} />,
        label: 'Daily Operations',
      });
    }
    if (can('view animal schedule')) {
      dashboardTaskChildren.push(
        {
          key: 'schedules/all',
          icon: <ScheduleOutlined style={iconStyle} />,
          label: t('common.tasksAndEvents') || 'Tasks & Events',
        },
        {
          key: 'tasks',
          icon: <CheckSquareOutlined style={iconStyle} />,
          label: t('common.tasks') || 'Tasks',
        },
      );
    }
    if (dashboardTaskChildren.length > 0) {
      items.push({
        key: 'dashboard-tasks',
        icon: <ScheduleOutlined style={parentIconStyle} />,
        label: t('common.schedulePlanning') || 'Schedule & Planning',
        children: dashboardTaskChildren,
      });
    }

    const fieldReportChildren = [];
    if (can('view alerts')) {
      fieldReportChildren.push({
        key: 'field-reports/alerts',
        icon: <AlertOutlined style={iconStyle} />,
        label: 'Dashboard',
      });
    }
    if (can('create alerts')) {
      fieldReportChildren.push(
        {
          key: 'field-reports/mine',
          icon: <UnorderedListOutlined style={iconStyle} />,
          label: 'My Alerts',
        },
        {
          key: 'field-reports/submit',
          icon: <FormOutlined style={iconStyle} />,
          label: 'Submit New Alert',
        },
      );
    }
    if (can('view alerts')) {
      fieldReportChildren.push({
        key: 'field-reports/qr',
        icon: <QrcodeOutlined style={iconStyle} />,
        label: 'Visitor Report QR',
      });
    }
    if (fieldReportChildren.length > 0) {
      items.push({
        key: 'field-reports-menu',
        icon: <FormOutlined style={parentIconStyle} />,
        label: 'Field Reports',
        children: fieldReportChildren,
      });
    }

    if (can('view herd tags')) {
      items.push({
        key: 'tagging-menu',
        icon: <TagOutlined style={parentIconStyle} />,
        label: 'Virtual Tags',
        children: [
          { key: 'tagging/dashboard', icon: <DashboardOutlined style={iconStyle} />, label: 'Dashboard' },
          { key: 'tagging/system', icon: <SyncOutlined style={iconStyle} />, label: 'System Tags' },
          { key: 'tagging/custom', icon: <FormOutlined style={iconStyle} />, label: 'Manual Tags' },
          { key: 'tagging/smart', icon: <ThunderboltOutlined style={iconStyle} />, label: 'Smart Tags' },
          { key: 'tagging/cohort', icon: <CalendarOutlined style={iconStyle} />, label: 'Cohort Tags' },
          { key: 'tagging/performance', icon: <ThunderboltOutlined style={iconStyle} />, label: 'Performance Tags' },
          { key: 'tagging/rules', icon: <ToolOutlined style={iconStyle} />, label: 'Rules & Config' },
        ],
      });
    }

    if (can('view animals')) {
      items.push({
        key: 'feedlot-menu',
        icon: <ApartmentOutlined style={parentIconStyle} />,
        label: 'Feedlot Management',
        children: [
          {
            key: 'feedlots/dashboard',
            icon: <DashboardOutlined style={iconStyle} />,
            label: t('common.feedlotDashboard') || 'Dashboard',
          },
          {
            key: 'feedlots/animals',
            icon: <GiCow style={iconStyle} />,
            label: t('common.feedlotBatchAnimals') || 'Feedlot Operation',
          },
          {
            key: 'feedlots',
            icon: <ApartmentOutlined style={iconStyle} />,
            label: t('common.feedlots') || 'Feedlot Batches',
          },
        ],
      });
    }

    if (canAny(['view milk inventory', 'view animal yields'])) {
      items.push({
        key: 'milking-menu',
        icon: <ThunderboltOutlined style={parentIconStyle} />,
        label: 'Milking',
        className: 'milking-menu-root',
        popupClassName: 'maveshi-milking-submenu-popup',
        children: [
          { key: 'milking', icon: <DashboardOutlined style={iconStyle} />, label: 'Milking overview & guide' },
          {
            type: 'group',
            key: 'milking-group-step-1',
            label: <MilkingMenuGroupLabel groupKey="milking-group-step-1" />,
            children: [
              { key: 'milking/tanks', icon: <InboxOutlined style={iconStyle} />, label: 'Chillers & Tanks' },
              { key: 'milking/routing', icon: <SwapOutlined style={iconStyle} />, label: 'Session → Tank Routing' },
              { key: 'milking/buyers', icon: <ShopOutlined style={iconStyle} />, label: 'Buyers & Pricing' },
            ],
          },
          {
            type: 'group',
            key: 'milking-group-step-2',
            label: <MilkingMenuGroupLabel groupKey="milking-group-step-2" />,
            children: [
              { key: 'animals/real-time-milking', icon: <ThunderboltOutlined style={iconStyle} />, label: 'Real-time milking' },
            ],
          },
          {
            type: 'group',
            key: 'milking-group-step-3',
            label: <MilkingMenuGroupLabel groupKey="milking-group-step-3" />,
            children: [
              { key: 'milking/dispositions', icon: <PlusOutlined style={iconStyle} />, label: 'Record Disposition' },
              { key: 'milking/calves', icon: <HeartOutlined style={iconStyle} />, label: 'Young stock & colostrum' },
              { key: 'milking/workers', icon: <TeamOutlined style={iconStyle} />, label: 'Worker & Farm Use' },
              { key: 'milking/processing', icon: <BuildOutlined style={iconStyle} />, label: 'On-Farm Processing' },
              { key: 'milking/wastage', icon: <WarningOutlined style={iconStyle} />, label: 'Wastage Records' },
            ],
          },
          {
            type: 'group',
            key: 'milking-group-step-4',
            label: <MilkingMenuGroupLabel groupKey="milking-group-step-4" />,
            children: [
              { key: 'milking/balance', icon: <UnorderedListOutlined style={iconStyle} />, label: 'Daily Milk Balance' },
              { key: 'milking-tab-balance', icon: <InboxOutlined style={iconStyle} />, label: 'Milk balance & tanks' },
              { key: 'milking/collections', icon: <ShoppingCartOutlined style={iconStyle} />, label: 'Collection Records' },
            ],
          },
          {
            type: 'group',
            key: 'milking-group-step-5',
            label: <MilkingMenuGroupLabel groupKey="milking-group-step-5" />,
            children: [
              { key: 'milking/tank-logs', icon: <CloudOutlined style={iconStyle} />, label: 'Temp & CIP logs' },
              { key: 'milking/quality', icon: <ExperimentOutlined style={iconStyle} />, label: 'Quality & Testing' },
            ],
          },
        ],
      });
    }

    if (can('view animal grazing')) {
      items.push({
        key: 'grazing-menu',
        icon: <EnvironmentOutlined style={parentIconStyle} />,
        label: 'Grazing',
        children: [
          { key: 'grazing', icon: <EnvironmentOutlined style={iconStyle} />, label: 'Dashboard' },
          { key: 'grazing/movement', icon: <SwapOutlined style={iconStyle} />, label: 'Record Movement' },
          { key: 'grazing/chart', icon: <LineChartOutlined style={iconStyle} />, label: 'Grazing Chart' },
          { key: 'grazing/planner', icon: <CalendarOutlined style={iconStyle} />, label: 'Rotation Planner' },
          { key: 'grazing/stocking', icon: <DashboardOutlined style={iconStyle} />, label: 'Stocking Rate' },
          { key: 'grazing/paddocks', icon: <UnorderedListOutlined style={iconStyle} />, label: 'Paddock Registry' },
          { key: 'grazing/setup', icon: <SettingOutlined style={iconStyle} />, label: 'Grazing Settings' },
        ],
      });
    }

    if (can('view drafting')) {
      items.push({
        key: 'drafting-menu',
        icon: <BranchesOutlined style={parentIconStyle} />,
        label: 'Drafting',
        children: [
          { key: 'drafting', icon: <DashboardOutlined style={iconStyle} />, label: 'Dashboard' },
          {
            type: 'group',
            key: 'drafting-group-operations',
            label: 'Operations',
            children: [
              { key: 'drafting/split', icon: <SwapOutlined style={iconStyle} />, label: 'Draft & split' },
              { key: 'drafting/merge', icon: <ApartmentOutlined style={iconStyle} />, label: 'Merge groups' },
              { key: 'drafting/joining', icon: <HeartOutlined style={iconStyle} />, label: 'Joining' },
              { key: 'drafting/yard-session', icon: <ScanOutlined style={iconStyle} />, label: 'Yard session' },
              { key: 'drafting/import', icon: <DownloadOutlined style={iconStyle} />, label: 'Import session file' },
            ],
          },
          {
            type: 'group',
            key: 'drafting-group-compliance',
            label: 'NLIS & PIC',
            children: [
              { key: 'drafting/nlis', icon: <SafetyCertificateOutlined style={iconStyle} />, label: 'NLIS & eNVD' },
              { key: 'drafting/pics', icon: <EnvironmentOutlined style={iconStyle} />, label: 'PIC directory' },
            ],
          },
          { key: 'drafting/history', icon: <HistoryOutlined style={iconStyle} />, label: 'Drafting history' },
        ],
      });
    }

    const pedigreeMenuChildren = [];
    if (can('view animal genealogy')) {
      pedigreeMenuChildren.push({
        key: 'genealogy/registry',
        icon: <UnorderedListOutlined style={iconStyle} />,
        label: 'Genealogy',
      });
    }
    if (can('view animal offspring')) {
      pedigreeMenuChildren.push({
        key: 'offspring/registry',
        icon: <UnorderedListOutlined style={iconStyle} />,
        label: 'Offspring',
      });
    }
    if (can('view animal siblings')) {
      pedigreeMenuChildren.push({
        key: 'siblings/registry',
        icon: <UnorderedListOutlined style={iconStyle} />,
        label: 'Sibling',
      });
    }
    if (pedigreeMenuChildren.length > 0) {
      items.push({
        key: 'pedigree-menu',
        icon: <BranchesOutlined style={parentIconStyle} />,
        label: 'Pedigree',
        children: pedigreeMenuChildren,
      });
    }

    if (can('view weather')) {
      items.push({
        key: 'weather-menu',
        icon: <CloudOutlined style={parentIconStyle} />,
        label: t('dashboard.weatherMenu') || 'Weather',
        children: [
          {
            key: 'weather',
            icon: <CloudOutlined style={iconStyle} />,
            label: t('dashboard.weather') || 'Weather',
          },
          {
            key: 'hli-forecast',
            icon: <LineChartOutlined style={iconStyle} />,
            label: t('dashboard.thiForecastMenu') || 'Heat Load (HLI)',
          },
          {
            key: 'thi-reference',
            icon: <InfoCircleOutlined style={iconStyle} />,
            label: t('dashboard.thiReferenceMenu') || 'THI Reference Guide',
          },
        ],
      });
    }

    if (canAny(['view animals', 'view animal breeding'])) {
      items.push({
        key: 'synchronization-menu',
        icon: <SyncOutlined style={parentIconStyle} />,
        label: 'Synchronization',
        children: [
          { key: 'synchronization', icon: <SyncOutlined style={iconStyle} />, label: 'Active Programs' },
          { key: 'synchronization/calendar', icon: <UnorderedListOutlined style={iconStyle} />, label: 'Protocol schedule' },
          { key: 'synchronization/templates', icon: <CopyOutlined style={iconStyle} />, label: 'Templates' },
          { key: 'synchronization/history', icon: <SafetyCertificateOutlined style={iconStyle} />, label: 'Compliance' },
        ],
      });
    }

    if (canAny(['view security', 'view visitors'])) {
      items.push({
        key: 'security-menu',
        icon: <SafetyCertificateOutlined style={parentIconStyle} />,
        label: t('common.securityBiosecurity') || 'Security & Biosecurity',
        children: [
          {
            key: 'security',
            icon: <DashboardOutlined style={iconStyle} />,
            label: t('common.overview') || 'Overview',
          },
          {
            key: 'security/patrol',
            icon: <LockOutlined style={iconStyle} />,
            label: 'Patrol',
          },
          {
            key: 'security/biosecurity',
            icon: <SafetyCertificateOutlined style={iconStyle} />,
            label: 'Biosecurity',
          },
          {
            key: 'security/headcount',
            icon: <TeamOutlined style={iconStyle} />,
            label: 'Headcount',
          },
          {
            key: 'security/visitors',
            icon: <UserOutlined style={iconStyle} />,
            label: 'Visitors',
          },
          {
            key: 'security/quarantine',
            icon: <MedicineBoxOutlined style={iconStyle} />,
            label: 'Quarantine Tracker',
          },
        ],
      });
    }

    // Inventory
    if (can('view inventory')) {
      items.push({
        key: 'inventory-menu',
        icon: <InboxOutlined style={parentIconStyle} />,
        label: t('common.inventory') || 'Inventory',
        children: [
          { key: 'inventory-dashboard', icon: <DashboardOutlined style={iconStyle} />, label: t('common.dashboard') || 'Dashboard' },
          { key: 'inventory', icon: <InboxOutlined style={iconStyle} />, label: t('common.inventoryItems') || 'Inventory Items' },
          { key: 'inventory/receive', icon: <TruckOutlined style={iconStyle} />, label: t('common.receiveStock') || 'Receive Stock' },
          { key: 'inventory/usage', icon: <UnorderedListOutlined style={iconStyle} />, label: t('common.usageLog') || 'Usage Log' },
          { key: 'inventory/batches', icon: <HistoryOutlined style={iconStyle} />, label: t('common.stockBatches') || 'Stock Batches' },
          { key: 'inventory/warehouses', icon: <HomeOutlined style={iconStyle} />, label: t('common.warehouses') || 'Warehouses' },
        ],
      });
    }

   
    // Staff & Teams
    const staffTeamChildren = [];
    if (canAny(['view users', 'create users'])) {
      staffTeamChildren.push(
        {
          key: 'staff-teams-dashboard',
          icon: <DashboardOutlined style={iconStyle} />,
          label: t('common.dashboard') || 'Dashboard',
        },
        { key: 'team', icon: <FaUsersCog style={iconStyle} />, label: t('common.team') || 'Team' }
      );
    }
    if (can('view roster')) {
      staffTeamChildren.push({ key: 'roster', icon: <CalendarOutlined style={iconStyle} />, label: t('common.roster') || 'Staff Roster' });
    }
    if (can('view payroll')) {
      staffTeamChildren.push({ key: 'payroll', icon: <DollarOutlined style={iconStyle} />, label: t('common.payroll') || 'Payroll' });
    }
    if (can('view contacts')) {
      staffTeamChildren.push({
        key: 'contacts',
        icon: <GiPerson style={iconStyle} />,
        label: t('common.contacts') || 'Contacts',
      });
    }
    if (staffTeamChildren.length > 0) {
      items.push({
        key: 'staff-teams',
        icon: <TeamOutlined style={parentIconStyle} />,
        label: t('common.staffTeams') || 'Staff & Teams',
        children: [
          ...staffTeamChildren,
         
        ],
      });
    }

    

    // Finance: farm finance analysis, then the books, then setup.
    const reportsChildren = [];
    if (can('view transactions')) {
      reportsChildren.push({
        key: 'finance-group-farm',
        type: 'group',
        label: geneticsGroupLabel('Farm finance', 'profit by group'),
        children: [
          { key: 'finance', icon: <DollarOutlined style={iconStyle} />, label: 'Finance dashboard' },
          { key: 'finance/groups', icon: <ApartmentOutlined style={iconStyle} />, label: 'Group P&L' },
          { key: 'finance/milk', icon: <FundOutlined style={iconStyle} />, label: 'Milk margin' },
          { key: 'finance/budget', icon: <PieChartOutlined style={iconStyle} />, label: 'Budget vs actual' },
          { key: 'finance/overhead', icon: <BankOutlined style={iconStyle} />, label: 'Overhead allocation' },
          { key: 'finance/compare', icon: <SwapOutlined style={iconStyle} />, label: 'Compare periods' },
        ],
      });
      reportsChildren.push({
        key: 'finance-group-costs',
        type: 'group',
        label: geneticsGroupLabel('Cost analysis', 'by enterprise'),
        children: [
          { key: 'finance/feedlot', icon: <LineChartOutlined style={iconStyle} />, label: 'Feedlot economics' },
          { key: 'finance/breeding', icon: <HeartOutlined style={iconStyle} />, label: 'Breeding costs' },
          { key: 'finance/health', icon: <MedicineBoxOutlined style={iconStyle} />, label: 'Health costs' },
          { key: 'finance/pasture', icon: <EnvironmentOutlined style={iconStyle} />, label: 'Pasture costs' },
          { key: 'finance/lifecycle', icon: <RiseOutlined style={iconStyle} />, label: 'Lifecycle cost' },
        ],
      });
    }
    const booksChildren = [];
    const canViewAccounts = canAny(['view transactions', 'view expenses', 'view revenues']);
    if (canViewAccounts) {
      booksChildren.push({ key: 'accounting-dashboard', icon: <DashboardOutlined style={iconStyle} />, label: 'Accounts dashboard' });
      if (can('view expenses')) {
        booksChildren.push({ key: 'other-charges', icon: <FallOutlined style={iconStyle} />, label: 'Other Charges' });
      }
    }
    if (can('view sales')) {
      booksChildren.push({ key: 'sales', icon: <ShoppingCartOutlined style={iconStyle} />, label: 'Sales' });
    }
    if (canViewAccounts) {
      booksChildren.push({ key: 'accounting-ledgers', icon: <HistoryOutlined style={iconStyle} />, label: 'Ledger Entries' });
    }
    if (booksChildren.length) {
      reportsChildren.push({ key: 'finance-group-books', type: 'group', label: geneticsGroupLabel('Books', 'sales & ledger'), children: booksChildren });
    }
    const setupChildren = [];
    if (can('view farms')) {
      setupChildren.push(
        { key: 'departments', icon: <GiOfficeChair style={iconStyle} />, label: t('common.departments') || 'Departments' },
        { key: 'cost-centres', icon: <GiMoneyStack style={iconStyle} />, label: t('common.costCentres') || 'Cost Centres' }
      );
    }
    if (can('view transactions')) {
      setupChildren.push({ key: 'chart-of-accounts', icon: <FaBook style={iconStyle} />, label: t('common.chartOfAccounts') || 'Chart of Accounts' });
    }
    if (setupChildren.length) {
      reportsChildren.push({ key: 'finance-group-setup', type: 'group', label: geneticsGroupLabel('Setup', 'accounts'), children: setupChildren });
    }
    if (reportsChildren.length > 0) {
      items.push({
        key: 'reports-finance',
        icon: <GiPieChart style={parentIconStyle} />,
        label: t('common.finance') || 'Finance',
        children: reportsChildren,
      });
    }

    // Maintenance
    if (can('view maintenance')) {
      items.push({
        key: 'maintenance-menu',
        icon: <ToolOutlined style={parentIconStyle} />,
        label: t('maintenance.maintenance') || 'Maintenance',
        children: [
          { key: 'maintenance', icon: <DashboardOutlined style={iconStyle} />, label: t('maintenance.dashboard') || 'Maintenance Dashboard' },
          { key: 'maintenance/equipment', icon: <ThunderboltOutlined style={iconStyle} />, label: t('maintenance.equipment') || 'Equipment' },
          { key: 'maintenance/schedules', icon: <CalendarOutlined style={iconStyle} />, label: t('maintenance.schedules') || 'Schedules' },
        ],
      });
    }

      // Farms & Locations
      if (can('view farms')) {
        items.push({
          key: 'farms-locations',
          icon: <GiBarn style={parentIconStyle} />,
          label: t('common.farmsLocations') || 'Farms & Locations',
          children: [
            { key: 'farms-locations-dashboard', icon: <DashboardOutlined style={iconStyle} />, label: t('common.dashboard') || 'Dashboard' },
            { key: 'locations', icon: <ApartmentOutlined style={iconStyle} />, label: 'Manage Locations' },
          ],
        });
      }

    return items;
  }, [can, canAny, isSuperAdmin, iconStyle, parentIconStyle, t, shouldDisableActions]);

  // Get all parent menu keys from menuItems
  const getAllParentKeys = useMemo(() => {
    return menuItems
      .filter(item => item.children && item.children.length > 0 && !item.type)
      .map(item => item.key);
  }, [menuItems]);

  const expandAllMenus = () => {
    setOpenKeys(getAllParentKeys);
  };

  const collapseAllMenus = () => {
    setOpenKeys([]);
  };

  // Filtered menu items based on search
  const filteredMenuItems = useMemo(() => {
    const filtered = filterMenuItems(menuItems, searchValue);
    // Disable menu items when account is locked (but keep them visible)
    return disableMenuItems(filtered);
  }, [menuItems, searchValue, shouldDisableActions]);

  // Activities dropdown menu items
  const activitiesMenuItems = translatedActivitiesList.map(activity => ({
    key: activity.key,
    icon: <span style={{ color: activity.color }}>{activity.icon}</span>,
    label: (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>{activity.label}</span>
        {activity.isNew && (
          <Tag size="small" color="green" style={{ marginLeft: 8, fontSize: '10px' }}>
            New
          </Tag>
        )}
      </div>
    ),
    onClick: () => {
      if (shouldDisableActions) {
        message.warning('Your account is locked. Please upgrade your package to unlock your account.');
        return;
      }
      navigate(`/activities?activity=${activity.key}`);
    },
  }));

  const userMenuItems = [
    {
      key: 'user-info',
      label: (
        <div style={{ padding: '12px 0' }}>
          <div style={{ fontWeight: 600, fontSize: '15px', color: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.85)' : '#495057', marginBottom: '4px' }}>
            {user?.name || 'User'}
          </div>
          <div style={{ fontSize: '13px', color: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.65)' : '#6c757d', marginBottom: '8px' }}>
            {user?.email}
          </div>
          {isSuperAdmin && (
            <Tag color="blue" icon={<CrownOutlined />} style={{ marginTop: 4, borderRadius: '6px' }}>
              Super Admin
            </Tag>
          )}
          {!isSuperAdmin && user?.roles?.length > 0 && (
            <Tag color="blue" style={{ marginTop: 4, borderRadius: '6px' }}>
              {user.roles[0]}
            </Tag>
          )}
        </div>
      ),
      disabled: true,
    },
    { type: 'divider' },
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('common.myProfile'),
      onClick: () => navigate('/me'),
    },
    ...(isInstallAvailable
      ? [{
          key: 'install-mobile-app',
          icon: <DownloadOutlined />,
          label: t('common.installMobileApp'),
          onClick: () => {
            installApp();
          },
        }]
      : []),
    {
      key: 'change-password',
      icon: <LockOutlined />,
      label: t('common.changePassword'),
      onClick: () => navigate('/change-password'),
    },
    // Only show package upgrade menu for non-super admins
    ...(!isSuperAdmin && isAccountOwner(user) ? [{
      key: 'package-upgrade',
      icon: <CrownOutlined />,
      label: t('common.packageUpgrade'),
      onClick: () => navigate('/upgrade'),
    }] : []),
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('common.settings'),
      onClick: () => {
        if (shouldDisableActions) {
          message.warning('Your account is disabled or locked. Please upgrade your account or contact admin.');
          return;
        }
        navigate('/settings');
      },
    },
    // Only show invoices menu for non-super admins
    ...(!isSuperAdmin && isAccountOwner(user) ? [{
      key: 'settings/invoices',
      icon: <FileTextOutlined />,
      label: t('common.invoices'),
      onClick: () => {
        if (shouldDisableActions) {
          message.warning('Your account is disabled or locked. Please upgrade your account or contact admin.');
          return;
        }
        navigate('/settings/invoices');
      },
    }] : []),
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('common.logout'),
      danger: true,
      onClick: async () => {
        const originalAdminToken = localStorage.getItem('original_admin_token');
        const translated = getSavedLanguage() !== 'en';

        i18n.changeLanguage('en');
        resetDisplayLanguage();
        localStorage.removeItem('menuOpenKeys');
        localStorage.removeItem('menuSelectedKeys');
        localStorage.removeItem('sidebarCollapsed');
        localStorage.removeItem('language');

        try {
          await logout(originalAdminToken);
        } catch (error) {
          console.error('Logout error:', error);
        }

        queryClient.clear();
        if (translated) {
          window.location.replace('/login');
          return;
        }
        navigate('/login', { replace: true });
      },
    },
  ];

  const handleMenuClick = ({ key, disabled }) => {
    // Show warning message if account is locked (except for upgrade page)
    if (shouldDisableActions && key !== 'upgrade' && key !== 'package-upgrade' && key !== '/upgrade') {
      message.warning('Your account is disabled or locked. Please upgrade your account or contact admin.');
      return;
    }

    // Also check for disabled prop (for other cases)
    if (disabled) {
      return;
    }

    if (key === '/') {
      navigate('/');
    } else if (key === 'feed-record') {
      navigate('/activities?activity=feedings');
    } else if (MILKING_MENU_ROUTE_OVERRIDES[key]) {
      navigate(MILKING_MENU_ROUTE_OVERRIDES[key]);
    } else {
      navigate(`/${key}`);
    }

    setSelectedKeys([key]);
    localStorage.setItem('menuSelectedKeys', JSON.stringify([key]));

    // Close mobile/tablet drawer after navigation
    if (isMobile || isTablet) {
      setMobileDrawerVisible(false);
    }
  };

  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  useEffect(() => {
    const sidebarExpanded = !collapsed && !isMobile && !isTablet;
    const prevOpenKeys = prevMenuOpenKeysRef.current;
    const justOpenedMilking =
      openKeys.includes('milking-menu') && !prevOpenKeys.includes('milking-menu');
    prevMenuOpenKeysRef.current = openKeys;

    if (!sidebarExpanded || !justOpenedMilking) return undefined;

    const frame = requestAnimationFrame(() => {
      const root = document.querySelector('.milking-menu-root');
      const menu = document.querySelector('.maveshi-sidebar-menu.ant-menu');
      if (!root || !menu) return;
      const offset = root.getBoundingClientRect().top - menu.getBoundingClientRect().top;
      menu.scrollTop += offset - 4;
    });

    return () => cancelAnimationFrame(frame);
  }, [openKeys, collapsed, isMobile, isTablet]);

  const openGlobalSearch = () => {
    if (shouldDisableActions) {
      message.warning('Your account is locked. Please upgrade your package to unlock your account.');
      return;
    }
    setGlobalSearchOpen(true);
  };

  const openGlobalSearchWithVoice = () => {
    openGlobalSearch();
    if (globalSearchListening) {
      globalSearchRef.current?.stopListening();
      return;
    }
    globalSearchRef.current?.startListening();
  };

  // Menu component to reuse
  const isCollapsedForMenu = collapsed && !isMobile && !isTablet;

  // Handle open change for collapsed menu (hover support)
  const handleOpenChangeCollapsed = (keys) => {
    // When collapsed, allow hover to open submenus
    setOpenKeys(keys);
  };

  const isCollapsedForBrand = collapsed && !isMobile && !isTablet;

  const logoComponent = (
    <div
      className={`sidebar-brand${isCollapsedForBrand ? ' sidebar-brand--collapsed' : ''}`}
      onClick={() => navigate('/')}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate('/');
        }
      }}
    >
      {isCollapsedForBrand ? (
        <div className="sidebar-brand-logo">
          <MaveshiBrandLogo size="sm" />
        </div>
      ) : (
        <div className="sidebar-brand-banner">
          <MaveshiBrandLogo variant="lockup" size="sm" wordmark="Maveshiwala" />
        </div>
      )}
    </div>
  );

  // Instant inline submenu open/close — avoids height animation shifting menus below
  const sidebarMenuMotion = useMemo(
    () => ({
      motionDeadline: 1,
      onAppearStart: () => ({ height: 0, opacity: 1 }),
      onEnterStart: () => ({ height: 0, opacity: 1 }),
      onAppearActive: (node) => ({ height: node?.scrollHeight ?? 0, opacity: 1 }),
      onEnterActive: (node) => ({ height: node?.scrollHeight ?? 0, opacity: 1 }),
      onLeaveStart: (node) => ({ height: node?.offsetHeight ?? 0, opacity: 1 }),
      onLeaveActive: () => ({ height: 0, opacity: 1 }),
      onAppearEnd: () => true,
      onEnterEnd: () => true,
      onLeaveEnd: () => true,
    }),
    []
  );

  // Collapsed hover popups: shift up/down to stay inside the viewport (long lists scroll via CSS)
  const collapsedPopupPlacements = useMemo(() => {
    const overflow = { adjustX: 1, adjustY: 1, shiftY: true };
    return {
      rightTop: { points: ['tl', 'tr'], overflow },
      rightBottom: { points: ['bl', 'br'], overflow },
    };
  }, []);

  const menuComponent = (
    <>
      <Menu
        mode="inline"
        selectedKeys={selectedKeys}
        openKeys={openKeys}
        onOpenChange={isCollapsedForMenu ? handleOpenChangeCollapsed : handleOpenChange}
        items={filteredMenuItems}
        onClick={handleMenuClick}
        theme={sidebarMenuTheme}
        className={`professional-menu-white modern-menu maveshi-sidebar-menu ${isCompact ? 'compact-menu' : ''}`}
        triggerSubMenuAction={isCollapsedForMenu ? "hover" : "click"}
        inlineCollapsed={isCollapsedForMenu}
        motion={sidebarMenuMotion}
        builtinPlacements={collapsedPopupPlacements}
        getPopupContainer={(node) => {
          // When collapsed, render popups in body to avoid overflow issues
          // When expanded, render in sidebar
          if (isCollapsedForMenu) {
            return document.body;
          }
          const sidebar = node.closest('.ant-layout-sider');
          return sidebar || document.body;
        }}
      />
    </>
  );

  const showSidebarMenuToolbar = !isCollapsedForMenu || isMobile || isTablet;

  const sidebarMenuToolbar = showSidebarMenuToolbar && getAllParentKeys.length > 0 ? (
    <div className="sidebar-menu-toolbar">
      <Button
        type="text"
        size="small"
        icon={<DownOutlined />}
        onClick={expandAllMenus}
        className="sidebar-toolbar-btn"
        title={t('common.expandAll') || 'Expand all'}
      >
        {t('common.expandAll') || 'Expand all'}
      </Button>
      <Button
        type="text"
        size="small"
        icon={<UpOutlined />}
        onClick={collapseAllMenus}
        className="sidebar-toolbar-btn"
        title={t('common.collapseAll') || 'Collapse all'}
        disabled={openKeys.length === 0}
      >
        {t('common.collapseAll') || 'Collapse all'}
      </Button>
    </div>
  ) : null;

  const sidebarSearchComponent = (collapsed && !isMobile && !isTablet) ? (
    <Button
      type="text"
      icon={<SearchOutlined />}
      onClick={openGlobalSearch}
      className="sidebar-search-collapsed-btn"
      title={t('common.globalSearch')}
    />
  ) : (
    <Input
      value={searchValue}
      onChange={(e) => setSearchValue(e.target.value)}
      placeholder={t('common.searchMenu')}
      prefix={<SearchOutlined style={{ color: 'rgba(255, 255, 255, 0.45)' }} />}
      allowClear
      size="middle"
      className="sidebar-search-input"
    />
  );

  const usesMobileShellHeader = isMobile || isTablet;
  const headerHeight = usesMobileShellHeader ? 56 : (isCompact ? 60 : 72);
  const expandedSidebarWidth = 264;
  const collapsedSidebarWidth = 72;
  const sidebarContentGap = 0;

  useEffect(() => {
    document.documentElement.style.setProperty('--layout-header-height', `${headerHeight}px`);
    return () => {
      document.documentElement.style.removeProperty('--layout-header-height');
    };
  }, [headerHeight]);

  const headerLeftOffset = isMobile || isTablet
    ? 0
    : ((collapsed ? collapsedSidebarWidth : expandedSidebarWidth) + sidebarContentGap);

  const returnToAdminLabel = t('adminUsers.returnToAdmin') || 'Return to Admin';
  const returnToAdminTooltip = isImpersonating && originalAdminUser?.name
    ? `${returnToAdminLabel} (${originalAdminUser.name})`
    : returnToAdminLabel;
  const isCompactHeader = isMobile || isTablet;

  return (
    <SyncProgramModalProvider>
    <AntLayout className="full-height-layout" style={{ minHeight: '100vh' }}>
      {/* Desktop Sidebar */}
      {!isMobile && !isTablet && (
        <Sider
          collapsible={true}
          theme={sidebarMenuTheme}
          width={expandedSidebarWidth}
          collapsedWidth={collapsedSidebarWidth}
          collapsed={collapsed}
          onCollapse={setCollapsed}
          breakpoint="lg"
          style={{
            overflow: 'auto',
            overflowX: 'visible',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 1000,
          }}
          className="professional-sidebar desktop-sidebar maveshi-sidebar"
        >
          <div className="sidebar-top-section">
            {logoComponent}
            <SidebarFarmSwitcher collapsed={isCollapsedForBrand} />
            <div className="sidebar-search-wrap">
              {sidebarSearchComponent}
            </div>
            {sidebarMenuToolbar}
          </div>
          {menuComponent}
        </Sider>
      )}

      {/* Mobile/Tablet Drawer */}
      {(isMobile || isTablet) && (
        <Drawer
          title={null}
          placement="left"
          onClose={() => setMobileDrawerVisible(false)}
          open={mobileDrawerVisible}
          bodyStyle={{
            padding: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
          classNames={{ body: 'maveshi-sidebar-drawer-body' }}
          width={expandedSidebarWidth}
          className="mobile-sidebar-drawer maveshi-sidebar"
          style={{ zIndex: 1200 }}
        >
          <div className="mobile-sidebar-drawer-layout">
            <div className="mobile-sidebar-drawer-brand-bar mobile-sidebar-drawer-brand-bar--compact">
              <span className="mobile-sidebar-drawer-title">{t('common.menu') || 'Menu'}</span>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                className="mobile-sidebar-drawer-close"
                aria-label={t('common.close')}
                onClick={() => setMobileDrawerVisible(false)}
              />
            </div>
            <div className="mobile-sidebar-drawer-menu-area">
              <div className="sidebar-top-section sidebar-top-section--below-brand">
                <SidebarFarmSwitcher />
                <div className="sidebar-search-wrap">
                  {sidebarSearchComponent}
                </div>
                {sidebarMenuToolbar}
              </div>
              {menuComponent}
            </div>
          </div>
        </Drawer>
      )}

      <AntLayout
        style={{
          marginLeft: isMobile || isTablet
            ? 0
            : ((collapsed ? collapsedSidebarWidth : expandedSidebarWidth) + sidebarContentGap),
          minHeight: '100vh'
        }}
        className="main-layout-content"
      >
        <Header
          style={{
            position: 'fixed',
            left: headerLeftOffset,
            top: 0,
            right: 0,
            zIndex: 1000,
            height: headerHeight,
          }}
          className={`maveshi-header responsive-header${
            isCompactHeader ? ' maveshi-header--compact-mobile' : ''
          }`}
        >
          {(isMobile || isTablet) && (
            <div className="maveshi-header-start">
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setMobileDrawerVisible(true)}
                className="header-icon-btn"
                aria-label="Open menu"
              />
              <button
                type="button"
                className="header-mobile-logo-btn"
                onClick={() => navigate('/')}
                aria-label="Maveshi home"
              >
                <MaveshiBrandLogo size="xs" className="header-mobile-logo" />
              </button>
            </div>
          )}

          {!isMobile && !isTablet && (
            <div className="header-center">
              <GlobalSearchBarTrigger
                onClick={openGlobalSearch}
                onVoiceClick={openGlobalSearchWithVoice}
                disabled={shouldDisableActions}
                listening={globalSearchListening}
              />
            </div>
          )}

          <div className="header-right">
            {isImpersonating && originalAdminUser && (
              <Tooltip title={returnToAdminTooltip} placement="bottomRight">
                <Button
                  type={isCompactHeader ? 'default' : 'primary'}
                  danger
                  icon={<SwapOutlined />}
                  onClick={returnToAdmin}
                  className={`header-action-btn header-return-admin-btn${isCompactHeader ? ' header-icon-btn is-compact' : ''}`}
                  aria-label={returnToAdminTooltip}
                >
                  {!isCompactHeader && (
                    <>
                      <span className="header-return-admin-label">{returnToAdminLabel}</span>
                      {originalAdminUser.name && (
                        <span className="header-return-admin-name">({originalAdminUser.name})</span>
                      )}
                    </>
                  )}
                </Button>
              </Tooltip>
            )}

            <div className="header-toolbar">
              {(can('view alerts') || isSuperAdmin) && (
                <NotificationCenterBell disabled={shouldDisableActions} />
              )}
              {(isMobile || isTablet) && (
                <GlobalSearchIconTrigger
                  onClick={openGlobalSearch}
                  disabled={shouldDisableActions}
                />
              )}
              {!isSuperAdmin && (
                <Dropdown
                  menu={{ items: activitiesMenuItems }}
                  placement="bottomRight"
                  overlayStyle={{ minWidth: 200, zIndex: 1200 }}
                  getPopupContainer={() => document.body}
                >
                  <Button
                    type="text"
                    icon={<ThunderboltOutlined />}
                    className="header-icon-btn"
                    title="Quick Activities"
                  />
                </Dropdown>
              )}
              <FarmAssistantChat
                enabled={!!user && !isSuperAdmin && isAccountOwner(user)}
                topOffset={headerHeight}
              />
              <Button
                type="text"
                icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={() => {
                  if (shouldDisableActions) {
                    message.warning('Your account is locked. Please upgrade your package to unlock your account.');
                    return;
                  }
                  toggleFullscreen();
                }}
                className="header-icon-btn"
                title={isFullscreen ? t('common.exitFullscreen') : t('common.enterFullscreen')}
              />
              {!isMobile && (
                <>
                  <span className="header-toolbar-divider" aria-hidden="true" />
                  <ThemeModeSwitcher buttonClassName="header-icon-btn" />
                  <ThemeSwitcher buttonClassName="header-icon-btn" />
                  <LanguageSwitcher buttonClassName="header-icon-btn" />
                </>
              )}
            </div>

            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              overlayStyle={{ minWidth: 220, zIndex: 1200 }}
              getPopupContainer={() => document.body}
            >
              <div className="header-user-pill" role="button" tabIndex={0}>
                <Avatar
                  icon={<UserOutlined />}
                  size={isMobile ? 'default' : 'large'}
                  className="header-user-avatar"
                />
                {!isMobile && (
                  <div className="header-user-meta">
                    <span className="header-user-name">{user?.name || 'User'}</span>
                    <span className="header-user-role">
                      {isSuperAdmin ? 'Super Admin' : (user?.email?.split('@')[0] || 'Account')}
                    </span>
                  </div>
                )}
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content
          style={{
            minHeight: `calc(100vh - ${headerHeight}px)`,
            margin: 0,
            marginTop: headerHeight,
          }}
        >
          <DemoFarmBanner enabled={!!user && !isSuperAdmin && isAccountOwner(user)} />
          {/* Pages are lazy-loaded: show a spinner in the content area while the menu stays usable */}
          <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', padding: '96px 0' }}><Spin /></div>}>
            {children}
          </Suspense>
        </Content>
      </AntLayout>

      <PwaInstallFab
        visible={showInstallFab}
        manualInstallMode={manualInstallMode}
        helpModalMode={helpModalMode}
        helpOpen={helpOpen}
        onHelpOpenChange={setHelpOpen}
        onInstall={installApp}
        onDismiss={() => dismissPromo()}
      />

      <GlobalSearch
        ref={globalSearchRef}
        open={globalSearchOpen}
        onOpenChange={setGlobalSearchOpen}
        disabled={shouldDisableActions}
        onListeningChange={setGlobalSearchListening}
      />
    </AntLayout>
    </SyncProgramModalProvider>
  );
};

export default Layout;
